import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { Button } from '@shared/ui/button';
import { Select, SelectOption } from '@shared/ui/select';
import { useAppSelector } from '../../../app/store/store';
import { selectUser } from '../../../entities/auth/model';
import { diagnosticsApi } from '../../../api/services/diagnostics.service';
import { isDiagnosticsStaff } from '@entities/diagnostics/lib';
import { DiagnosticTicketsTable } from '@widgets/diagnostic-tickets-table';
import { DiagnosticTicketCreateModal } from '@features/diagnostic-ticket-create';
import type {
  DiagnosticTicket,
  DiagnosticTypeInfo,
} from '../../../types/diagnosticsInterfaces';

const PAGE_SIZE = 20;
const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const selectControlStyles = { control: () => ({ height: '44px', minHeight: '44px' }) };

/** Вкладка «На диагностике»: список диагностических тикетов + создание. */
export default function DiagnosticsTab() {
  const navigate = useNavigate();
  const me = useAppSelector(selectUser);
  const isStaff = isDiagnosticsStaff(me);

  const [types, setTypes] = useState<DiagnosticTypeInfo[]>([]);
  const [typeFilter, setTypeFilter] = useState<SelectOption | null>(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<DiagnosticTicket[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    diagnosticsApi.getTypes().then((r) => setTypes(r.data)).catch(() => setTypes([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await diagnosticsApi.getTickets({
        ticket_type: typeFilter ? Number(typeFilter.value) : undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ordering: '-created_at',
      });
      setRows(data.results);
      setCount(data.count);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Не удалось загрузить обращения');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // клиент видит только «приём»; фильтр по типу — для персонала
  const typeOptions: SelectOption[] = useMemo(
    () => types.map((t) => ({ value: t.ticket_type.id, label: t.ticket_type.name })),
    [types],
  );

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          {isStaff && typeOptions.length > 1 && (
            <div className="w-[300px] min-h-[72px] flex flex-col">
              <label className={FIELD_LABEL}>Тип</label>
              <Select
                placeholder="Все типы"
                options={typeOptions}
                isVisible={false}
                customStyles={selectControlStyles}
                optionSelected={typeFilter}
                isClearable
                onSelected={(o) => {
                  setPage(1);
                  setTypeFilter(o);
                }}
              />
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Button onClick={() => setCreateOpen(true)}>
              <span className="inline-flex items-center gap-1">
                <FiPlus /> {isStaff ? 'Принять оборудование' : 'Отправить на диагностику'}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4">
          {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
          <DiagnosticTicketsTable
            rows={rows}
            loading={loading}
            isStaff={isStaff}
            onRowClick={(t) => navigate(`/devices/diagnostics/${t.id}`)}
          />
        </div>
        {count > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ‹ Назад
            </button>
            <span className="text-sm text-gray-500 px-2">
              Стр. {page} из {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Вперёд ›
            </button>
          </div>
        )}
      </div>

      <DiagnosticTicketCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(t) => {
          setCreateOpen(false);
          navigate(`/devices/diagnostics/${t.id}`);
        }}
      />
    </>
  );
}
